/**
 * First-login terms acceptance modal — PRD §7.5.
 * Renders as a blocking modal after sign-in if the user has not yet
 * accepted the terms. Requires tick + Accept before continuing.
 *
 * Stores acceptance in:
 *   - localStorage: 'scope-tos-accepted' = '1'  (prevents re-show on next load)
 *   - Server: calls user.acceptTerms or clientContact.acceptTerms tRPC procedure
 *
 * Pre-authorised deviation: server-side persistance added via user.acceptTerms
 * and clientContact.acceptTerms procedures (Phase 5 Agent L).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { trpc } from '../lib/trpc.js';
import { useAuth } from '../lib/auth.js';
import { routes } from '../routes.js';

const TOS_KEY = 'scope-tos-accepted';

interface TermsAcceptanceModalProps {
  /** Called when acceptance is confirmed — parent unblocks the UI. */
  onAccepted: () => void;
}

export function TermsAcceptanceModal({ onAccepted }: TermsAcceptanceModalProps) {
  const { auth } = useAuth();
  const [ticked, setTicked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const acceptUserTerms = trpc.user.acceptTerms.useMutation();
  const acceptContactTerms = trpc.clientContact.acceptTerms.useMutation();

  async function handleAccept() {
    if (!ticked) {
      setError('Please tick the checkbox to confirm you have read and agree to the terms.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      if (auth.kind === 'agency') {
        await acceptUserTerms.mutateAsync();
      } else if (auth.kind === 'contact') {
        await acceptContactTerms.mutateAsync();
      }
      markTosAccepted();
      onAccepted();
    } catch {
      setError('Failed to record acceptance. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-scope-navy/60 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tos-modal-title"
    >
      <div className="w-full max-w-md rounded-xl bg-white shadow-scope-lg">
        {/* Header */}
        <div className="border-b border-scope-border px-6 py-5">
          <h2 id="tos-modal-title" className="text-lg font-semibold text-scope-navy">
            Welcome to Scope
          </h2>
          <p className="mt-1 text-sm text-scope-text-muted">
            Before you continue, please review and accept our terms.
          </p>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <div className="rounded-lg border border-scope-border-soft bg-scope-cream p-4 text-sm text-scope-text-muted">
            <p>
              By using Scope, you agree to the{' '}
              <Link
                to={routes.terms}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-scope-navy underline hover:no-underline"
              >
                Terms of Service
              </Link>{' '}
              and acknowledge that you have read our{' '}
              <Link
                to={routes.privacy}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-scope-navy underline hover:no-underline"
              >
                Privacy Policy
              </Link>
              .
            </p>
            <p className="mt-2">
              We use your data only to provide the Scope service. All data is stored in the EU. You
              can request deletion of your data at any time.
            </p>
          </div>

          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={ticked}
              onChange={(e) => {
                setTicked(e.target.checked);
                if (e.target.checked) setError('');
              }}
              className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border border-scope-border accent-scope-navy focus:ring-2 focus:ring-scope-green"
            />
            <span className="text-sm text-scope-navy">
              I have read and agree to the Terms of Service and Privacy Policy.
            </span>
          </label>

          {error && (
            <p className="rounded-md bg-status-red/10 px-3 py-2 text-xs text-status-red">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end border-t border-scope-border px-6 py-4">
          <button
            onClick={() => void handleAccept()}
            disabled={submitting}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-scope-navy px-5 py-2.5 text-sm font-medium text-white transition hover:bg-scope-navy/90 focus:outline-none focus:ring-2 focus:ring-scope-green disabled:opacity-60"
          >
            {submitting ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : null}
            Accept &amp; Continue
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Check if the current user has accepted ToS on this device.
 * localStorage is only an optimistic fast-path — the server's
 * termsAcceptedAt is the source of truth (see RequireToS).
 */
export function tosAccepted(): boolean {
  try {
    return localStorage.getItem(TOS_KEY) === '1';
  } catch {
    return false;
  }
}

/** Record local acceptance (fast-path so we don't re-query on next load). */
export function markTosAccepted(): void {
  try {
    localStorage.setItem(TOS_KEY, '1');
  } catch {
    // ignore — server flag still gates correctly
  }
}
